/**
 * Os ícones da Personagem: os seis atributos e os secundários.
 *
 * <p>Mesma regra do `PericiaIcone`, e de propósito — as três seções da tela têm que
 * parecer o mesmo sistema. Desenho em 24×24, só traço, silhueta que sobrevive ao
 * encolher, e a cor vindo de fora: <b>forma e cor identificam juntas</b>, então quem não
 * distingue as cores ainda distingue as formas.
 *
 * <p>Nenhum desenho aqui repete um de perícia. O atributo Agilidade é um raio, e não uma
 * silhueta correndo, porque correndo seria a Esquiva; a Vitalidade é uma folha de vida e
 * não um coração cheio, porque coração é o HP logo abaixo. Ícone repetido em duas seções
 * da mesma página faz o olho concluir que são a mesma coisa.
 */
interface Props {
  nome: string;
  cor?: string;
  tamanho?: number;
  titulo?: string;
}

const comum = (cor: string, tamanho: number) => ({
  width: tamanho,
  height: tamanho,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: cor,
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

export function AtributoIcone({ nome, cor = 'currentColor', tamanho = 18, titulo }: Props) {
  return (
    <svg {...comum(cor, tamanho)} role="img" aria-label={titulo ?? nome}>
      {titulo && <title>{titulo}</title>}
      {atributo(nome, cor)}
    </svg>
  );
}

function atributo(sigla: string, cor: string) {
  switch (sigla) {
    // Braço flexionado: o que carrega e o que quebra.
    case 'FOR':
      return (
        <>
          <path d="M4 18v-3a5 5 0 015-5h3" />
          <path d="M12 10V6a3 3 0 016 0v6a8 8 0 01-8 8H4" />
        </>
      );

    // Mão aberta com os dedos separados: o trabalho fino.
    case 'DES':
      return (
        <>
          <path d="M8 12V5a1.5 1.5 0 013 0v6" />
          <path d="M11 11V4a1.5 1.5 0 013 0v7" />
          <path d="M14 11V6a1.5 1.5 0 013 0v8a7 7 0 01-7 7 6 6 0 01-6-6v-5a1.5 1.5 0 013 0" />
        </>
      );

    // Broto de duas folhas: o que aguenta e rebrota.
    case 'VIT':
      return (
        <>
          <path d="M12 21v-9" />
          <path d="M12 12C12 8 9 5 5 5c0 4 3 7 7 7z" />
          <path d="M12 14c0-3.5 2.5-6 6-6 0 3.5-2.5 6-6 6z" />
        </>
      );

    // Raio: reação. Correndo seria a Esquiva, que é perícia.
    case 'AGI':
      return <path d="M13 2L5 13h6l-2 9 9-12h-6z" />;

    // Olho: notar o que está ali.
    case 'PER':
      return (
        <>
          <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
          <circle cx="12" cy="12" r="2.5" fill={cor} />
        </>
      );

    // Livro aberto: o que se sabe e o que se deduz.
    case 'INT':
      return (
        <>
          <path d="M12 6C9.5 4 6.5 3.5 3 4v14c3.5-.5 6.5 0 9 2" />
          <path d="M12 6c2.5-2 5.5-2.5 9-2v14c-3.5-.5-6.5 0-9 2" />
          <path d="M12 6v14" />
        </>
      );

    default:
      return <circle cx="12" cy="12" r="7" />;
  }
}

export function DerivadoIcone({ nome, cor = 'currentColor', tamanho = 18, titulo }: Props) {
  return (
    <svg {...comum(cor, tamanho)} role="img" aria-label={titulo ?? nome}>
      {titulo && <title>{titulo}</title>}
      {derivado(nome, cor)}
    </svg>
  );
}

function derivado(id: string, cor: string) {
  switch (id) {
    // Degraus subindo: o que a barra exige a cada nível.
    case 'xp-do-nivel':
      return (
        <>
          <path d="M3 20h5v-5H3zM9.5 20h5V9h-5zM16 20h5V4h-5z" />
        </>
      );

    // Estrela cheia: o ponto que o nível entrega.
    case 'pontos-por-nivel':
      return <path d="M12 3l2.6 5.6 6 .8-4.4 4.2 1.1 6.1-5.3-2.9-5.3 2.9 1.1-6.1L3.4 9.4l6-.8z" fill={cor} />;

    // Moeda: o que se paga por um ponto.
    case 'custo-do-ponto':
      return (
        <>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 8v8M9.5 10h5M9.5 14h5" />
        </>
      );

    // Coração: a barra que zera a luta.
    case 'hp':
      return <path d="M12 20s-7-4.4-7-9.3A3.8 3.8 0 0112 8a3.8 3.8 0 017 2.7c0 4.9-7 9.3-7 9.3z" />;

    // Gota: o combustível do que não é físico.
    case 'sp':
      return <path d="M12 3s6 6.4 6 10.4a6 6 0 01-12 0C6 9.4 12 3 12 3z" />;

    // Sol com ponteiro: o dia, contado em turnos.
    case 'turnos':
      return (
        <>
          <circle cx="12" cy="12" r="7" />
          <path d="M12 12V8M12 12l3 2" />
          <path d="M12 1v2M12 21v2M1 12h2M21 12h2" />
        </>
      );

    // Lâmina rachando: o que o golpe tira quando chega.
    case 'dano':
      return (
        <>
          <path d="M4 20L14 6l1 5 5 1L6 22z" />
          <path d="M15 4l1-2 1 2 2 1-2 1-1 2-1-2-2-1z" fill={cor} />
        </>
      );

    // Mochila: o que se carrega, e o quanto ainda cabe.
    case 'peso-carregado':
      return (
        <>
          <path d="M6 8h12v11a2 2 0 01-2 2H8a2 2 0 01-2-2z" />
          <path d="M9 8V5a3 3 0 016 0v3" />
          <path d="M10 14h4" />
        </>
      );

    // Lua: a noite, e o que ela devolve.
    case 'qualidade-do-sono':
      return <path d="M20 14A8.5 8.5 0 019.5 3.5 8.5 8.5 0 1020 14z" />;

    // Frasco com brilho: o efeito ativo, enquanto durar.
    case 'condicao':
      return (
        <>
          <circle cx="12" cy="13" r="6" />
          <path d="M12 7V3M9 3h6" />
          <path d="M10 13a2 2 0 012-2" />
        </>
      );

    // Ampulheta: quem age primeiro.
    case 'iniciativa':
      return (
        <>
          <path d="M6 3h12M6 21h12" />
          <path d="M7 3c0 5 5 6 5 9s-5 4-5 9" />
          <path d="M17 3c0 5-5 6-5 9s5 4 5 9" />
        </>
      );

    // Alvo com a flecha no centro: o que o golpe precisa vencer.
    case 'acerto':
      return (
        <>
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="3.5" />
          <circle cx="12" cy="12" r="1" fill={cor} />
        </>
      );

    // Peitoral: o que a armadura absorve.
    case 'defesa':
      return (
        <>
          <path d="M5 5l7-2 7 2v6c0 5-3 8-7 10-4-2-7-5-7-10z" />
          <path d="M9 9h6M9 13h6" />
        </>
      );

    /*
     * Esquiva e Bloqueio não desenham nada próprio: elas usam a perícia de mesmo nome, e
     * a tela mostra o ícone dela. Desenhar um segundo símbolo para o mesmo conceito
     * negaria justamente a ligação que a linha declara.
     */
    default:
      return <circle cx="12" cy="12" r="7" />;
  }
}
